/* @flow */
import { Record, OrderedMap } from 'immutable';
import { List } from 'immutable';
import { pipe, pick } from 'ramda';

import { SharingGroup } from 'modules/groups/types.js';

export type Action = { type: string, payload: any };

export type SectorRecord = Record<{
  id: number,
  name: string,
  label: string,
}>;

type EnclaveObject = {
  id: number,
  name: string,
};

type QuotaShape = {
  guid: ?string,
  lastResetTime: ?number, // Timestamp
  maxRequests: number,
  usedRequests: number,
  windowSize: number,
};

export const Quota: Record<QuotaShape> = Record({
  guid: null,
  lastResetTime: null,
  maxRequests: 0,
  usedRequests: 0,
  windowSize: null, // Null allows the server to use the default
});

// Raw from the server
export type RawCompanyObject = {
  id: number,
  guid: string,
  name: string,
  enclaves: ?List<EnclaveObject>,
  sharingGroups: ?List<SharingGroup>,
  sector: Object,
  primaryEmail: string,
  address: string,
  city: string,
  state: string,
  zipCode: string,
  country: string,
  created: number,
  users: [],
  userCount: number,
  trialCompany: boolean,
  requestQuotas: ?List<QuotaShape>,
};

export type CompanyRecord = Record<{
  id: number,
  guid: string,
  maxUserCount: number,
  userCount: number,
  sector: SectorRecord,
  city: string,
  name: string,
  zipCode: string,
  state: string,
  address: string,
  enclaves: [],
  sharingGroups: [],
  country: string,
  created: number,
  primaryEmail: string,
  mfaActivated: boolean,
  ssoActivated: boolean,
  trialCompany: boolean,
  requestQuotas: List<QuotaShape>,
}>;

export type CompanyState = OrderedMap<string, CompanyRecord>;

export const Sector = Record({
  id: null,
  name: '',
  label: ''
});

export const EnclavePermission = Record({
  rowId: null,
  id: null,
  name: '',
  read: false,
  create: false,
  update: false,
  pattern: null,
});

export const Company = Record({
  id: null, // Is a number, must be provided by server
  guid: '',
  maxUserCount: 5, // Should come from server
  userCount: 0,
  sector: null,
  city: '',
  name: '',
  zipCode: '',
  state: '',
  address: '',
  enclaves: List(),
  sharingGroups: List(),
  country: '',
  primaryEmail: '',
  created: null, // Timestamp
  maxIngestEmailsCount: 1,
  mfaActivated: false,
  ssoActivated: false,
  trialCompany: false,
  rateLimit: undefined,
  requestQuotas: undefined,
});

// Support instantiation of sector record
Company.of = (x: any): CompanyRecord => {
  x = Company(x);
  x = x.set('sector', Sector(x.sector));
  x = x.set('sharingGroups', x.sharingGroups.map(SharingGroup));
  x = x.set('requestQuotas', x.requestQuotas ? x.requestQuotas.map(Quota) : undefined);
  return x;
};

export type OptionsRecord = Record<{
  query: string,
  enclaveId: string,
  sortBy: string,
  sortOrder: string,
  pageNumber: number,
  pageSize: number,
  hasNext: boolean,
  empty: boolean,
  pageNumber: number,
  totalElements: number,
  totalPages: number,
}>;

export const Options = Record({
  query: '',
  enclaveId: '',
  sortBy: 'name',
  sortOrder: 'ASC',
  pageNumber: 0,
  pageSize: 25,
  hasNext: true,
  empty: true,
  totalElements: 0,
  totalPages: 0,
  simpleFetch: false,
});

export const getQuotaParams = pipe(
  Quota,
  pick(['maxRequests', 'usedRequests', 'windowSize'])
);



// WEBPACK FOOTER //
// ./src/app/modules/companies/types.js